import { useState } from "react";
import { getFormattedTimestamp } from "@/lib/time";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_DIGITS_MIN = 7;
const PHONE_DIGITS_MAX = 15;

/**
 * CMS proxy endpoint. The Apps Script URL itself lives only inside the CMS source —
 * the public JS bundle never knows about it.
 */
const SUBMIT_ENDPOINT = `${import.meta.env.VITE_CMS_URL ?? ""}/api/invite-speaker-submit`;

type Country = { code: string; dial: string; name: string };

const COUNTRIES: Country[] = [
  { code: "IN", dial: "+91", name: "India" },
  { code: "US", dial: "+1", name: "United States" },
  { code: "GB", dial: "+44", name: "United Kingdom" },
  { code: "AE", dial: "+971", name: "United Arab Emirates" },
  { code: "AU", dial: "+61", name: "Australia" },
  { code: "SG", dial: "+65", name: "Singapore" },
  { code: "CA", dial: "+1", name: "Canada" },
];

function flagEmoji(code: string): string {
  return code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

export default function InviteForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [designation, setDesignation] = useState("");
  const [company, setCompany] = useState("");
  const [countryCode, setCountryCode] = useState("IN");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState("");

  const selected =
    COUNTRIES.find((c) => c.code === countryCode) ?? COUNTRIES[0];

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Full name is required.";
    else if (name.trim().length < 2)
      next.name = "Name must be at least 2 characters.";

    if (!email.trim()) next.email = "Work email is required.";
    else if (!EMAIL_RE.test(email.trim()))
      next.email = "Please enter a valid email address.";

    if (!company.trim()) next.company = "Company name is required.";

    const digits = phone.replace(/\D/g, "");
    if (!digits) next.phone = "Phone number is required.";
    else if (digits.length < PHONE_DIGITS_MIN || digits.length > PHONE_DIGITS_MAX)
      next.phone = `Phone number must be ${PHONE_DIGITS_MIN}–${PHONE_DIGITS_MAX} digits.`;

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setApiError("");
    if (!validate()) return;

    window.fbq?.("track", "Lead");

    setLoading(true);
    try {
      const payload = {
        fullName: name.trim(),
        email: email.trim(),
        designation: designation.trim(),
        companyName: company.trim(),
        phone: `${selected.dial} ${phone.trim()}`,
        countryCode: selected.code,
        timestamp: getFormattedTimestamp(),
      };

      const res = await fetch(SUBMIT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        setApiError(data.error ?? "Could not submit right now. Please try again.");
        return;
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Invite-as-speaker submission error", err);
      setApiError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section
      id="invite-as-speaker-cta"
      className="w-full bg-black py-14 font-fira text-white sm:py-20"
    >
      <div className="mx-auto w-[90%] md:w-[80%]">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-12 lg:gap-16">
          <header className="text-left">
            <p className="mb-3 text-xs font-normal uppercase tracking-[1.75px] text-white/80 md:text-sm">
              Get in touch
            </p>
            <h2 className="font-fira-condensed text-[30px] font-medium leading-tight tracking-tight text-white md:text-[42px]">
              Tell us about the event.
            </h2>
          </header>

          {submitted ? (
            <div className="flex flex-col items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
                <svg
                  className="h-6 w-6 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="font-fira-condensed text-[22px] font-semibold text-white">
                Thanks for reaching out
              </h3>
              <p className="text-sm leading-relaxed text-[#FFFFFF85]">
                We&apos;ve got your invite. Vaibhav&apos;s team will be in
                touch shortly.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              noValidate
              className="flex flex-col gap-5"
            >
              <Field id="invite-name" label="Full Name" required error={errors.name}>
                <input
                  id="invite-name"
                  type="text"
                  placeholder="Enter your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  aria-invalid={!!errors.name}
                  className={inputClasses(!!errors.name)}
                />
              </Field>

              <Field
                id="invite-email"
                label="Work Email"
                required
                error={errors.email}
              >
                <input
                  id="invite-email"
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email id"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={!!errors.email}
                  className={inputClasses(!!errors.email)}
                />
              </Field>

              <Field id="invite-designation" label="Designation">
                <input
                  id="invite-designation"
                  type="text"
                  placeholder="Enter your Designation"
                  value={designation}
                  onChange={(e) => setDesignation(e.target.value)}
                  className={inputClasses(false)}
                />
              </Field>

              <Field
                id="invite-company"
                label="Company Name"
                required
                error={errors.company}
              >
                <input
                  id="invite-company"
                  type="text"
                  placeholder="Enter your Company name"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  aria-invalid={!!errors.company}
                  className={inputClasses(!!errors.company)}
                />
              </Field>

              <Field
                id="invite-phone"
                label="Phone Number"
                required
                error={errors.phone}
              >
                <div className="flex items-stretch gap-2">
                  <div className="relative">
                    <label htmlFor="invite-country" className="sr-only">
                      Country
                    </label>
                    <select
                      id="invite-country"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="h-full appearance-none rounded-lg border border-white/10 bg-[#1A1A1A] px-3 pr-8 font-fira text-sm text-white focus:border-white/40 focus:outline-none"
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {flagEmoji(c.code)} {c.dial}
                        </option>
                      ))}
                    </select>
                    <span
                      className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-white/50"
                      aria-hidden
                    >
                      ▾
                    </span>
                  </div>
                  <input
                    id="invite-phone"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder={`${selected.dial} XXXXXXXXXX`}
                    value={phone}
                    onChange={(e) =>
                      setPhone(e.target.value.replace(/[^\d+\s-]/g, ""))
                    }
                    aria-invalid={!!errors.phone}
                    className={`${inputClasses(!!errors.phone)} flex-1`}
                  />
                </div>
              </Field>

              {apiError ? (
                <p
                  role="alert"
                  aria-live="polite"
                  className="rounded-lg border border-red-400/20 bg-red-500/10 px-3 py-2 text-center text-xs text-red-400"
                >
                  {apiError}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={loading}
                className="mt-2 w-full rounded-lg bg-white px-6 py-3.5 font-fira text-[15px] font-semibold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? "Submitting…" : "Send enquiry →"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function inputClasses(hasError: boolean): string {
  return `w-full rounded-lg border bg-[#1A1A1A] px-4 py-3 font-fira text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-white/40 transition-colors ${
    hasError ? "border-red-400/50" : "border-white/10"
  }`;
}

function Field({
  id,
  label,
  required,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-2 block font-fira text-sm font-semibold text-white"
      >
        {label}
        {required ? <span className="text-red-400">*</span> : null}
      </label>
      {children}
      {error ? (
        <p className="mt-1 text-xs text-red-400" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
